import classNames from 'classnames'
import { Link } from 'gatsby'
import React, {
  useRef,
  FC,
  useState,
  useContext,
  PropsWithChildren,
} from 'react'

import { useQueryParams } from '../../hooks/useQueryParams'
import {
  inLocaleNames,
  LocaleContext,
  localeLanguage,
  MeasurLocale,
} from '../../locale/i18n'
import { ISlugLink } from '../../locale/pathing'
import useOnClickOutside from '../../logic/useOnClickOutside'
import IconCaret from '../icons/IconCaret'

import { CurrencySelector } from './CurrencySelector'
import { SelectorOption, Selector } from './Selector'

import './LocaleSelector.scss'

interface Props {
  localisedSlugs: ISlugLink[]
  storeShowLocaleHint: (showLocaleHint: boolean) => void
}

const renderLanguage = (locale: MeasurLocale) =>
  localeLanguage(locale)?.toUpperCase()

export const LocaleSelectorMobile: FC<Props> = ({
  localisedSlugs,
  storeShowLocaleHint,
}) => {
  const { locale } = useContext(LocaleContext)
  const { appendSearch } = useQueryParams()
  return (
    <div className="localeSelector localeSelector--mobile">
      {localisedSlugs
        .filter(alt => alt.locale !== locale)
        .map(alt => (
          <Link
            className="localeSelector__option localeSelector__option--mobile"
            key={alt.locale}
            to={appendSearch(alt.slug)}
            onClick={() =>
              alt.locale === MeasurLocale.FINLAND_FINNISH &&
              storeShowLocaleHint(false)
            }
          >
            {inLocaleNames[alt.locale]}
          </Link>
        ))}
      <CurrencySelector />
    </div>
  )
}

export const LocaleSelectorDesktop = ({
  localisedSlugs,
  storeShowLocaleHint,
  children,
}: PropsWithChildren<Props>) => {
  const { locale, t } = useContext(LocaleContext)
  const { appendSearch } = useQueryParams()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  // For useOutsideClick
  const ref = useRef<HTMLDivElement>(null)

  useOnClickOutside(ref, () => {
    setIsOpen(false)
  })

  return (
    <div ref={ref} className="localeSelector localeSelector--desktop">
      <button
        className="localeSelector__desktopSelector"
        onClick={() => {
          setIsOpen(!isOpen)
          if (locale !== MeasurLocale.FINLAND_FINNISH) {
            storeShowLocaleHint(false)
          }
        }}
      >
        <div className="localeSelector__text">{renderLanguage(locale)}</div>
        <IconCaret width="10" height="10" open={isOpen} />
      </button>
      {isOpen ? (
        <div className="navbar__popup localeSelector__popup">
          <Selector heading={t(`localeSelector.language`)}>
            {localisedSlugs.map(alt => (
              <SelectorOption
                as={Link}
                className={classNames(
                  alt.locale === locale && 'selector__option--selected',
                )}
                key={alt.locale}
                to={appendSearch(alt.slug)}
                onClick={() => {
                  setIsOpen(false)
                  storeShowLocaleHint(false)
                }}
              >
                {inLocaleNames[alt.locale]}
              </SelectorOption>
            ))}
          </Selector>
          <hr className="localeSelector__hr" />
          <CurrencySelector
            heading={t(`localeSelector.currency`)}
            onSelect={() => setIsOpen(false)}
          />
        </div>
      ) : null}
      {children}
    </div>
  )
}
